import { useState, useEffect } from "react";
import { FaEye, FaTrash, FaExchangeAlt } from "react-icons/fa";
import axios from "axios";
import { showToast } from "../../Components/utils/ShowToast";
import { Link } from "react-router-dom";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { MdOpenInNew } from "react-icons/md";
import Dropdown from "../../Components/utils/ReuseDropdown";
import ConfirmDialog from "../../Components/utils/ReuseConfirmDialog";
import Loader from "../../Components/utils/Loader";
import SearchInput from "../../Components/utils/ReuseSearchInput";

function AdminProducts({ pre }) {
    const [products, setProducts] = useState([]);
    const [filterType, setFilterType] = useState("All");
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductDetailsModel, setShowProductDetailsModel] = useState(false);
    const [openedProduct, setOpenedProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: "",
        message: "",
        onConfirm: null,
    });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState("title");
    const [listingApproval, setListingApproval] = useState(false);

    const productFilters = ["All", "Marketplace", "B2B", "Discounted", "B2B Active", "B2B Inactive"];
    const searchFilters = ["Title", "ID", "Seller", "Company"];
    const searchTypeMap = {
        Title: "title",
        ID: "id",
        Seller: "seller",
        Company: "company",
    };

    useEffect(() => {
        fetchProducts();
        fetchListingSettings();
    }, []);

    const fetchListingSettings = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/listing-config`);
            setListingApproval(Boolean(res.data?.listings?.approvalRequired));
        } catch {
            // Toggle simply shows its default if this fails.
        }
    };

    /**
     * Turning this on holds *new* listings for review. Existing live listings
     * are untouched, so enabling it cannot black out the storefront.
     */
    const updateListingApproval = async (enabled) => {
        const token = localStorage.getItem("adminToken");
        try {
            await axios.post(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/update/listings`,
                { listings: { approvalRequired: enabled } },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setListingApproval(enabled);
            showToast(
                enabled
                    ? "New listings will now be held for review before going live."
                    : "New listings will go live immediately.",
                "success"
            );
        } catch (e) {
            showToast(e.response?.data?.message || "Failed to update setting", "error");
        }
    };

    const fetchProducts = async () => {
        setLoading(true);
        const token = localStorage.getItem("adminToken");

        try {
            const [marketplaceRes, b2bRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/products/all/`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/listings`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            const marketplaceProducts = (marketplaceRes.data.products || []).map((product) => ({
                ...product,
                isB2B: false,
                marketLabel: "Marketplace",
                // Listings created before moderation existed have no status; they
                // are live, so read them as active.
                status: product.status || "active",
            }));

            const b2bListings = (b2bRes.data.listings || []).map((listing) => ({
                _id: listing._id,
                title: listing.title,
                description: listing.description,
                maincategory: listing.maincategory,
                subcategory: listing.subcategory,
                childcategory: listing.childcategory,
                thumbnail: listing.thumbnail,
                images: listing.images || [],
                rating: listing.rating || 0,
                orders: listing.unitsSold || 0,
                impressions: listing.impressions || 0,
                clicks: listing.clicks || 0,
                createdAt: listing.createdAt,
                pricingType: "custom",
                isB2B: true,
                marketLabel: "B2B",
                status: listing.status,
                listingMode: listing.listingMode,
                listingType: listing.listingType,
                moq: listing.moq,
                variations: listing.variations || [],
                priceTiers: listing.priceTiers || [],
                minOrderValue: listing.minOrderValue,
                fulfillmentTime: listing.fulfillmentTime,
                sellerCountry: listing.sellerCountry,
                b2bSellerProfile: listing.b2bSellerId || null,
                sellerId: listing.b2bSellerId?.sellerId || null,
            }));

            const merged = [...marketplaceProducts, ...b2bListings].sort(
                (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
            );

            setProducts(merged);
        } catch (e) {
            showToast(e.response?.data?.error || "Failed to load listings", "error");
        }

        setLoading(false);
    };

    useEffect(() => {
        let filtered = products;

        if (filterType !== "All") {
            switch (filterType) {
                case "Marketplace":
                    filtered = filtered.filter((item) => !item.isB2B);
                    break;
                case "B2B":
                    filtered = filtered.filter((item) => item.isB2B);
                    break;
                case "Discounted":
                    filtered = filtered.filter((item) => !item.isB2B && item.discounts && item.discounts.length > 0);
                    break;
                case "B2B Active":
                    filtered = filtered.filter((item) => item.isB2B && item.status === "active");
                    break;
                case "B2B Inactive":
                    filtered = filtered.filter((item) => item.isB2B && item.status === "inactive");
                    break;
                default:
                    break;
            }
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            switch (searchType) {
                case "title":
                    filtered = filtered.filter((item) => (item.title || "").toLowerCase().includes(query));
                    break;
                case "id":
                    filtered = filtered.filter((item) => (item._id || "").toLowerCase().includes(query));
                    break;
                case "seller":
                    filtered = filtered.filter((item) =>
                        (
                            item.sellerId?.userId?.username ||
                            item.b2bSellerProfile?.userId?.username ||
                            item.sellerId?.displayName ||
                            ""
                        )
                            .toLowerCase()
                            .includes(query)
                    );
                    break;
                case "company":
                    filtered = filtered.filter((item) =>
                        (item.b2bSellerProfile?.companyName || "").toLowerCase().includes(query)
                    );
                    break;
                default:
                    break;
            }
        }

        setFilteredProducts(filtered);
    }, [filterType, products, searchQuery, searchType]);

    const handleOpenProductDetails = (product) => {
        setOpenedProduct(product);
        setShowProductDetailsModel(true);
    };

    const handleDeleteProduct = (product) => {
        setConfirmDialog({
            open: true,
            title: product.isB2B ? "Delete B2B Listing" : "Delete Product",
            message: product.isB2B
                ? "Are you sure you want to delete this B2B listing? This action cannot be undone."
                : "Are you sure you want to delete this product? This action cannot be undone.",
            onConfirm: () => confirmDeleteProduct(product),
        });
    };

    const confirmDeleteProduct = async (product) => {
        setDeleteLoading(true);
        const token = localStorage.getItem("adminToken");

        try {
            const endpoint = product.isB2B
                ? `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/listings/${product._id}`
                : `${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/product/delete/${product._id}`;

            const response = await axios.delete(endpoint, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.success) {
                setProducts((prev) => prev.filter((item) => item._id !== product._id));
                showToast(product.isB2B ? "B2B listing deleted successfully" : "Product deleted successfully", "success");
                if (openedProduct?._id === product._id) {
                    setShowProductDetailsModel(false);
                    setOpenedProduct(null);
                }
            }
        } catch (e) {
            showToast(e.response?.data?.error || "Failed to delete listing", "error");
        }

        setDeleteLoading(false);
        setConfirmDialog((prev) => ({ ...prev, open: false }));
    };

    const handleToggleB2BStatus = async () => {
        if (!openedProduct?.isB2B) return;

        const token = localStorage.getItem("adminToken");
        const nextStatus = openedProduct.status === "active" ? "inactive" : "active";
        setStatusLoading(true);

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/listings/${openedProduct._id}/status`,
                { status: nextStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const updatedListing = response.data.listing;
            setProducts((prev) =>
                prev.map((item) =>
                    item._id === openedProduct._id
                        ? {
                            ...item,
                            status: updatedListing.status,
                        }
                        : item
                )
            );
            setOpenedProduct((prev) => ({ ...prev, status: updatedListing.status }));
            showToast(`B2B listing marked as ${updatedListing.status}`, "success");
        } catch (e) {
            showToast(e.response?.data?.error || "Failed to update listing status", "error");
        }

        setStatusLoading(false);
    };

    /**
     * Hide or restore a marketplace listing.
     *
     * Deletion was previously the only option, which is irreversible and takes
     * the seller's photos, copy and reviews with it. Hiding is reversible and
     * tells the seller what to fix.
     */
    const handleModerateProduct = async (nextStatus) => {
        if (!openedProduct || openedProduct.isB2B) return;

        let note = "";
        if (nextStatus === "inactive" || nextStatus === "rejected") {
            note = window.prompt("Why is this listing being taken down? The seller is emailed this reason.") || "";
            if (!note.trim()) {
                showToast("A reason is required — the seller is told why.", "error");
                return;
            }
        }

        const token = localStorage.getItem("adminToken");
        setStatusLoading(true);

        try {
            await axios.put(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/product/moderate/${openedProduct._id}`,
                { status: nextStatus, note: note.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setProducts((prev) =>
                prev.map((item) => (item._id === openedProduct._id ? { ...item, status: nextStatus } : item))
            );
            setOpenedProduct((prev) => ({ ...prev, status: nextStatus }));
            showToast(`Listing marked as ${nextStatus}`, "success");
        } catch (e) {
            showToast(e.response?.data?.message || "Failed to update listing status", "error");
        }

        setStatusLoading(false);
    };

    const sellerName = (product) => {
        if (product.isB2B) {
            return product.b2bSellerProfile?.companyName || product.sellerId?.displayName || "B2B Seller";
        }
        return product.sellerId?.userId?.username || "N/A";
    };

    const sellerLinkId = (product) => {
        if (product.isB2B) return product.sellerId?._id || null;
        return product.sellerId?._id || null;
    };

    const getB2BListingLabel = (listingMode) => {
        if (listingMode === 'both') return 'B2B Wholesale + Customization';
        if (listingMode === 'wholesale') return 'B2B Wholesale';
        if (listingMode === 'customization') return 'B2B Customization';
        return 'B2B';
    };

    const productElems = loading ? (
        <Loader type="simpleMini" />
    ) : filteredProducts.length > 0 ? (
        filteredProducts.map((product, index) => (
            <div key={product._id || index} className="requestRow row">
                <div className="titleField field">
                    <p className="title">{product.title}</p>
                    {product.isB2B && (
                        <span className="adminListingTypeTag">{getB2BListingLabel(product.listingMode)}</span>
                    )}
                </div>

                {sellerLinkId(product) ? (
                    <Link to={`/vendorDetail/${sellerLinkId(product)}`} className="sellerField field">
                        {sellerName(product) + " >"}
                    </Link>
                ) : (
                    <p className="sellerField field">{sellerName(product)}</p>
                )}

                <p className="field">
                    {product.maincategory || "-"}
                </p>

                <p className="pricingField field">
                    {product.isB2B ? "Tiered" : product.pricingType === "fixed" ? "Fixed" : "Variable"}
                </p>

                <p className="ratingField field">{Number(product.rating || 0).toFixed(1)}</p>
                <p className="soldField field">{product.orders || 0}</p>

                <div className="actionsField field">
                    <FaEye className="icon" onClick={() => handleOpenProductDetails(product)} />
                    <FaTrash className="icon delete" onClick={() => handleDeleteProduct(product)} />
                    <MdOpenInNew
                        className="icon"
                        onClick={() =>
                            window.open(
                                `${process.env.REACT_APP_Frontend_Host_Name}/productDetail/${product._id}${
                                    product.isB2B ? "?market=b2b" : ""
                                }`,
                                "_blank"
                            )
                        }
                    />
                </div>
            </div>
        ))
    ) : (
        <div className="row">Nothing to show here...</div>
    );

    return (
        <div className="adminProductsDiv">
            <div className="adminProductsContent">
                <div className="adminListingApprovalToggle">
                    <div>
                        <h4>Review New Listings Before They Go Live</h4>
                        <p>
                            {listingApproval
                                ? "Enabled: new marketplace listings stay hidden until an admin approves them."
                                : "Disabled: new marketplace listings are visible to buyers immediately."}
                            {" "}Listings that are already live are not affected.
                        </p>
                    </div>
                    <label className="adminBusinessSwitch">
                        <input
                            type="checkbox"
                            checked={listingApproval}
                            onChange={(e) => updateListingApproval(e.target.checked)}
                        />
                        <span className="adminBusinessSlider" />
                    </label>
                </div>

                <div className="tableDiv">
                    <div className="tableContent">
                        <div className="upper">
                            <h2 className="secondaryHeading">
                                <span>{pre === "dashboard" ? "Listings" : filterType} </span>
                                {pre === "dashboard" ? "Management" : "Listings"}
                            </h2>
                            <div className="upperRight">
                                <SearchInput
                                    searchType={searchType}
                                    setSearchType={setSearchType}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    searchFilters={searchFilters}
                                    searchTypeMap={searchTypeMap}
                                    placeholder={`Search by ${
                                        searchType === "id"
                                            ? "ID"
                                            : searchType === "seller"
                                            ? "Seller"
                                            : searchType === "company"
                                            ? "Company"
                                            : "Title"
                                    }`}
                                />
                                <Dropdown options={productFilters} selected={filterType} onSelect={setFilterType} />
                                {pre === "dashboard" && (
                                    <Link to="/chwi-admin/products-services" className="secondaryBtn">
                                        View All {">"}
                                    </Link>
                                )}
                            </div>
                        </div>
                        <div className="header">
                            <p className="title">Title</p>
                            <p>Seller</p>
                            <p>Category</p>
                            <p>Pricing</p>
                            <p>Rating</p>
                            <p>Orders</p>
                            <p>Actions</p>
                        </div>
                        <div className="rows">{productElems}</div>
                    </div>
                </div>
            </div>

            {showProductDetailsModel && openedProduct && (
                <div className="popupDiv addNewModelDiv">
                    <div className="popupContent">
                        <div className="form">
                            <h2 className="secondaryHeading">
                                Listing <span>Details</span>
                            </h2>

                            <div className="rows">
                                <div className="row">
                                    <div>Title</div>
                                    <div className="fw600">{openedProduct.title || "N/A"}</div>
                                </div>
                                <div className="rowCol">
                                    <div>Description</div>
                                    <div className="fw600">{openedProduct.description || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>ID</div>
                                    <div className="fw600">{openedProduct._id}</div>
                                </div>
                                <div className="row">
                                    <div>Main Category</div>
                                    <div className="fw600">{openedProduct.maincategory || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Sub Category</div>
                                    <div className="fw600">{openedProduct.subcategory || "N/A"}</div>
                                </div>
                                {openedProduct.isB2B && (
                                    <>
                                        <div className="row">
                                            <div>Company</div>
                                            <div className="fw600">{openedProduct.b2bSellerProfile?.companyName || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Country / City</div>
                                            <div className="fw600">
                                                {(openedProduct.b2bSellerProfile?.country || "-") + " / " + (openedProduct.b2bSellerProfile?.city || "-")}
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div>Listing Mode</div>
                                            <div className="fw600">{openedProduct.listingMode || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Listing Type</div>
                                            <div className="fw600">{openedProduct.listingType || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>MOQ</div>
                                            <div className="fw600">{openedProduct.moq || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Status</div>
                                            <div className="fw600">{openedProduct.status || "N/A"}</div>
                                        </div>
                                    </>
                                )}
                                {!openedProduct.isB2B && (
                                    <>
                                        <div className="row">
                                            <div>Ingredients</div>
                                            <div className="fw600">{openedProduct.ingredients || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Allergy Info</div>
                                            <div className="fw600">{openedProduct.allergyInfo || "N/A"}</div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">
                                Pricing <span>Details</span>
                            </h2>
                            <div className="rows">
                                <div className="row">
                                    <div>Pricing Type</div>
                                    <div className="fw600">
                                        {openedProduct.isB2B
                                            ? "Tiered"
                                            : openedProduct.pricingType === "fixed"
                                            ? "Fixed"
                                            : "Variable"}
                                    </div>
                                </div>

                                {openedProduct.isB2B && (
                                    <>
                                        <div className="rowCol">
                                            <div>Price Tiers</div>
                                            <div className="fw500">
                                                {(openedProduct.priceTiers || []).length > 0
                                                    ? openedProduct.priceTiers.map((tier, idx) => (
                                                          <div key={idx}>
                                                              {tier.minQty} - {tier.maxQty || "max"} units: ${tier.price}
                                                          </div>
                                                      ))
                                                    : "N/A"}
                                            </div>
                                        </div>
                                        <div className="rowCol">
                                            <div>Variations</div>
                                            <div className="fw500">
                                                {(openedProduct.variations || []).length > 0
                                                    ? openedProduct.variations.map((variant, idx) => (
                                                          <div key={idx}>
                                                              <span className="fw600">{variant.name}</span>: {(variant.options || []).join(", ")}
                                                          </div>
                                                      ))
                                                    : "N/A"}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {!openedProduct.isB2B && openedProduct.discounts && openedProduct.discounts.length > 0 && (
                                    <div className="row">
                                        <div>Discounts</div>
                                        <div className="fw600">
                                            {openedProduct.discounts.map((discount, idx) => (
                                                <div key={idx}>
                                                    {discount.quantity} units: {discount.discount}% off
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">
                                Performance <span>Metrics</span>
                            </h2>
                            <div className="rows">
                                <div className="row">
                                    <div>Rating</div>
                                    <div className="fw600">{Number(openedProduct.rating || 0).toFixed(1)}</div>
                                </div>
                                <div className="row">
                                    <div>Impressions</div>
                                    <div className="fw600">{openedProduct.impressions || 0}</div>
                                </div>
                                <div className="row">
                                    <div>Clicks</div>
                                    <div className="fw600">{openedProduct.clicks || 0}</div>
                                </div>
                                <div className="row">
                                    <div>{openedProduct.isB2B ? "Units Sold" : "Orders"}</div>
                                    <div className="fw600">{openedProduct.orders || 0}</div>
                                </div>
                            </div>

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">
                                Listing <span>Images</span>
                            </h2>
                            <div className="productImages">
                                {openedProduct.thumbnail && <img src={openedProduct.thumbnail} alt="Thumbnail" />}
                                {openedProduct.images &&
                                    openedProduct.images.map((image, index) => (
                                        <img key={index} src={image} alt={`Product ${index + 1}`} />
                                    ))}
                            </div>

                            <div className="buttonsDiv">
                                {!openedProduct.isB2B && (
                                    <button
                                        className="secondaryBtn"
                                        type="button"
                                        onClick={() => handleModerateProduct(openedProduct.status === "active" ? "inactive" : "active")}
                                        disabled={statusLoading}
                                    >
                                        <FaExchangeAlt />
                                        {statusLoading
                                            ? "Updating..."
                                            : openedProduct.status === "active"
                                            ? "Hide from buyers"
                                            : "Make visible"}
                                    </button>
                                )}
                                {openedProduct.isB2B && (
                                    <button className="secondaryBtn" type="button" onClick={handleToggleB2BStatus} disabled={statusLoading}>
                                        <FaExchangeAlt />
                                        {statusLoading
                                            ? "Updating..."
                                            : openedProduct.status === "active"
                                            ? "Mark Inactive"
                                            : "Mark Active"}
                                    </button>
                                )}
                                <button className="secondaryBtn" type="button" onClick={() => setShowProductDetailsModel(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowProductDetailsModel(false)} />
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
                isLoading={deleteLoading}
            />
        </div>
    );
}

export default AdminProducts;
